import { inferPlanId } from "../data/featureTiers";
import { isKnownPlatformOwnerUid } from "./platformOwnerService";

const normalizeKey = (value) => String(value || "").trim().toLowerCase();

export const isBusinessWorkspaceMemberProfile = (accountProfile = {}) =>
  !(
    accountProfile?.platformOwner === true ||
    accountProfile?.lifetimeAdminGrant === true ||
    accountProfile?.accessSource === "lifetime_admin" ||
    accountProfile?.accountBillingType === "lifetimeAdmin" ||
    accountProfile?.isFounder === true ||
    accountProfile?.accountBillingType === "founderLifetime"
  ) &&
  (
    accountProfile?.accountBillingType === "companySeat" ||
    (
      ["business", "company"].includes(normalizeKey(accountProfile?.accountType)) &&
      normalizeKey(accountProfile?.role) !== "owner" &&
      Boolean(accountProfile?.companyId || accountProfile?.companyOwnerUid || accountProfile?.activeWorkspaceId)
    )
  );

export const isLifetimeAdminAccess = ({ user, accountProfile, isPlatformOwner = false } = {}) =>
  Boolean(user?.uid) &&
  (
    isPlatformOwner === true ||
    isKnownPlatformOwnerUid(user.uid) ||
    accountProfile?.platformOwner === true ||
    accountProfile?.lifetimeAdminGrant === true ||
    accountProfile?.accessSource === "lifetime_admin" ||
    accountProfile?.accountBillingType === "lifetimeAdmin"
  );

export const buildLifetimeAdminAccessState = ({ user, accountProfile } = {}) => ({
  signedIn: Boolean(user),
  isSubscribed: true,
  isTrialActive: false,
  isCompanySeat: false,
  isFounder: true,
  isLifetimeAdmin: true,
  isBillingExempt: true,
  hasActiveTrelloAccess: true,
  workspaceAccess: {
    entitled: true,
    source: "lifetime_admin",
    companyId: accountProfile?.activeWorkspaceId || accountProfile?.companyId || user?.uid || "",
    sponsorUid: accountProfile?.companyOwnerUid || user?.uid || "",
    billingRequiredForMember: false,
  },
  standaloneAccess: {
    entitled: true,
    source: "lifetime_admin",
    billingRequired: false,
  },
  isElevatedAccess: true,
  accessSource: "lifetime_admin",
  accountType: "business",
  role: "owner",
  planId: "business",
  hasAccess: Boolean(user),
  canAccessAdmin: Boolean(user),
  offlineMessage:
    "Lifetime admin access is verified by Firebase UID and is independent of Stripe, Trello, trials, workspace membership, and billing state.",
});

export const buildWorkspaceSponsoredAccess = (accountProfile = {}) => {
  if (!isBusinessWorkspaceMemberProfile(accountProfile)) {
    return {
      entitled: false,
      source: "none",
      companyId: "",
      sponsorUid: "",
      billingRequiredForMember: true,
    };
  }

  const companyId =
    accountProfile.activeWorkspaceId ||
    accountProfile.companyId ||
    accountProfile.companyOwnerUid ||
    "";

  return {
    entitled: Boolean(companyId),
    source: "business_workspace_member",
    companyId,
    sponsorUid: accountProfile.companyOwnerUid || companyId,
    billingRequiredForMember: false,
  };
};

export const getAccessState = ({
  user,
  trialStatus,
  subscriptionStatus,
  accountProfile,
  isPlatformOwner = false,
}) => {
  if (isLifetimeAdminAccess({ user, accountProfile, isPlatformOwner })) {
    return buildLifetimeAdminAccessState({ user, accountProfile });
  }

  const normalizedSubscriptionStatus = String(subscriptionStatus || "").toLowerCase();
  const isSubscribed =
    normalizedSubscriptionStatus === "active" ||
    normalizedSubscriptionStatus === "trialing";
  const isTrialActive = Boolean(trialStatus?.isTrialActive);
  const isFounder =
    isPlatformOwner === true ||
    accountProfile?.platformOwner === true ||
    accountProfile?.lifetimeAdminGrant === true ||
    accountProfile?.accessSource === "lifetime_admin" ||
    accountProfile?.accountBillingType === "lifetimeAdmin" ||
    accountProfile?.isFounder === true ||
    accountProfile?.accountBillingType === "founderLifetime";
  const workspaceAccess = buildWorkspaceSponsoredAccess(accountProfile);
  const isCompanySeat = workspaceAccess.entitled;
  const isBillingExempt =
    !isCompanySeat &&
    (
      accountProfile?.billingExempt === true ||
      accountProfile?.subscriptionRequired === false
    );
  const isElevatedAccess = isFounder || isBillingExempt;
  const accessSource = isFounder ?
    (accountProfile?.accessSource === "lifetime_admin" ||
      accountProfile?.lifetimeAdminGrant === true ||
      accountProfile?.accountBillingType === "lifetimeAdmin" ?
      "lifetime_admin" :
      "founder") :
    isSubscribed ?
      "standalone_subscription" :
      isCompanySeat ?
        "business_workspace_member" :
      isBillingExempt ?
        "promotion" :
          "none";

  return {
    signedIn: Boolean(user),
    isSubscribed,
    isTrialActive,
    isCompanySeat,
    isFounder,
    isBillingExempt,
    hasActiveTrelloAccess: false,
    workspaceAccess,
    standaloneAccess: {
      entitled: Boolean(isSubscribed || isTrialActive || isFounder || isBillingExempt),
      source: isCompanySeat ? "none" : accessSource,
      billingRequired: !(isSubscribed || isTrialActive || isFounder || isBillingExempt),
    },
    isElevatedAccess,
    accessSource,
    accountType: accountProfile?.accountType || "personal",
    role: accountProfile?.role || "personal",
    planId: inferPlanId({
      accountProfile: {
        ...accountProfile,
        isFounder,
        platformOwner: isPlatformOwner || accountProfile?.platformOwner,
      },
      subscriptionStatus,
      trialStatus,
    }),
    hasAccess: Boolean(user) && (
      isSubscribed ||
      isTrialActive ||
      isCompanySeat ||
      isElevatedAccess
    ),
    offlineMessage:
      "Offline mode can load the app shell, but subscription and trial status must be verified online before unrestricted use.",
  };
};

